import { useMemo, useState } from 'react';
import { ReloadOutlined, HomeOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import logo from '../../assets/img/wc-logo-big.png';
import './errorFallback.css';

// Local-only incident id so a user can quote something stable to support.
// Not a server error digest — just a correlation hint.
function generateIncidentId() {
    return `ERR-${Date.now().toString(36).toUpperCase()}`;
}

export function ErrorFallback() {
    const incidentId = useMemo(generateIncidentId, []);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(incidentId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (_) {
            // clipboard API unavailable — fail silently, it's a non-critical affordance
        }
    };

    return (
        <div className="error-fallback" role="alert">
            <div className="error-fallback__backdrop" aria-hidden="true">
                <span className="error-fallback__blob error-fallback__blob--teal" />
                <span className="error-fallback__blob error-fallback__blob--orange" />
                <span className="error-fallback__dots" />
            </div>

            <div className="error-fallback__content">
                <img src={logo} alt="Workforce City" className="error-fallback__logo" />

                <div className="error-fallback__illustration" aria-hidden="true">
                    <svg viewBox="0 0 200 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="100" cy="85" r="80" fill="url(#efGlow)" />

                        <circle className="error-fallback__spark error-fallback__spark--1" cx="38" cy="112" r="4" fill="#76DFD2" />
                        <circle className="error-fallback__spark error-fallback__spark--2" cx="168" cy="54" r="3" fill="#00BFA5" />
                        <circle className="error-fallback__spark error-fallback__spark--3" cx="58" cy="24" r="2.5" fill="#FF8F52" />

                        <g transform="rotate(-4 100 77)">
                            <rect x="30" y="30" width="140" height="95" rx="14" fill="#EBF0F7" stroke="#33475C" strokeWidth="3" />
                            <line x1="30" y1="54" x2="170" y2="54" stroke="#33475C" strokeOpacity="0.35" strokeWidth="2" />
                            <circle cx="44" cy="42" r="3.5" fill="#76DFD2" />
                            <circle cx="56" cy="42" r="3.5" fill="#FF8F52" />

                            <path
                                className="error-fallback__crack"
                                d="M150 40 L128 62 L144 71 L108 101 L119 116 L86 132"
                                stroke="#FF7A4C"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </g>

                        <g className="error-fallback__badge">
                            <path d="M145 100 L172 143 L118 143 Z" fill="url(#efOrange)" stroke="#FFFFFF" strokeWidth="6" strokeLinejoin="round" />
                            <rect x="141.5" y="115" width="7" height="17" rx="3.5" fill="#FFFFFF" />
                            <circle cx="145" cy="137" r="4" fill="#FFFFFF" />
                        </g>

                        <defs>
                            <radialGradient id="efGlow" cx="0.5" cy="0.42" r="0.6">
                                <stop offset="0%" stopColor="#E6FBF7" />
                                <stop offset="100%" stopColor="#E6FBF7" stopOpacity="0" />
                            </radialGradient>
                            <linearGradient id="efOrange" x1="118" y1="100" x2="172" y2="143" gradientUnits="userSpaceOnUse">
                                <stop offset="0%" stopColor="#f85884" />
                                <stop offset="100%" stopColor="#fb8c5f" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>

                <p className="error-fallback__eyebrow">Unexpected error</p>
                <h1 className="error-fallback__title">Something went wrong</h1>
                <p className="error-fallback__subtitle">
                    We hit a snag loading this page. Reloading usually clears it up — if it keeps
                    happening, send our team the reference below and we'll dig in.
                </p>

                <div className="error-fallback__actions">
                    <button type="button" className="error-fallback__btn-primary" onClick={() => window.location.reload()}>
                        <ReloadOutlined /> Reload page
                    </button>
                    <a className="error-fallback__btn-secondary" href="/">
                        <HomeOutlined /> Go to homepage
                    </a>
                </div>

                <button type="button" className="error-fallback__reference" onClick={handleCopy}>
                    <span>Reference: {incidentId}</span>
                    {copied ? <CheckOutlined /> : <CopyOutlined />}
                </button>
            </div>
        </div>
    );
}
